import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {always} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  useDeleteLabourRateMutation,
  usePostLabourRateSetAsActiveMutation,
  usePostLabourRateSetAsInActiveMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function LabourRates() {
  const navigate = useNavigate();

  const [deleteLabourRate] = useDeleteLabourRateMutation();
  const [postLabourRateSetAsActive] = usePostLabourRateSetAsActiveMutation();
  const [postLabourRateSetAsInActive] = usePostLabourRateSetAsInActiveMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('redirectDetail', 'edit', () =>
        navigate(composePath(settingsRoutes.labourRatesDetail, {params: {id}}))
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteLabourRate({labourRateId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('setAsActive', () =>
        postLabourRateSetAsActive({labourRateId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .with('setAsInactive', () =>
        postLabourRateSetAsInActive({labourRateId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .otherwise(always(undefined));
  };

  return (
    <>
      <Helmet title={i18n.t('entity.workshop.labels.labourRates')} />
      <SettingsTemplate
        header={{
          title: i18n.t('entity.workshop.labels.labourRates'),
          actions: [
            {
              type: 'button',
              title: i18n.t('entity.workshop.actions.newLabourRate'),
              onClick: () => navigate(settingsRoutes.labourRatesCreate),
            },
          ],
        }}
        data-testid={testIds.settings.labourRates('template')}
      >
        <DmsDataGrid
          gridCode="labour-rate"
          actionCallback={actionCallback}
          data-testid={testIds.settings.labourRates('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
